import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useApi from '../api';
import { toast } from 'react-toastify';
import { formatDate } from '../utils/dateFormatter';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LoadingState } from '@/components/ui/LoadingState';
import { ArrowLeft, Eye } from 'lucide-react';

interface AdminLeadResponse {
  id: string;
  organization_id?: string | null;
  organization_name?: string | null;
  agent_id?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  phone?: string | null;
  company?: string | null;
  job_title?: string | null;
  source?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  first_seen_at?: string | null;
  last_seen_at?: string | null;
  last_activity_at?: string | null;
  provider_profiles?: Record<string, unknown> | null;
  canonical_person?: Record<string, unknown> | null;
  canonical_company?: Record<string, unknown> | null;
  conversation_count?: number | null;
  visitor_profile_count?: number | null;
  [key: string]: unknown;
}

interface LeadConversation {
  id: string;
  name?: string | null;
  type?: string | null;
  status?: string | null;
  agent_id?: string | null;
  organization_id?: string | null;
  organization_name?: string | null;
  lead_id?: string | null;
  visitor_id?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  message_count?: number;
}

interface LeadVisitorProfile {
  id: string;
  visitor_id?: string | null;
  organization_name?: string | null;
  first_seen_at?: string | null;
  last_seen_at?: string | null;
  conversation_count?: number | null;
  session_count?: number | null;
}

interface LeadDetailResponse {
  lead: AdminLeadResponse;
  conversations: LeadConversation[];
  visitor_profiles: LeadVisitorProfile[];
  browser_event_count: number;
}

const renderJsonBlock = (data: unknown) => (
  <pre className="text-xs bg-muted p-3 rounded-lg overflow-auto max-h-[600px]">
    {JSON.stringify(data, null, 2)}
  </pre>
);

const hasJsonContent = (data: unknown): boolean => {
  if (!data || typeof data !== 'object') return false;
  return Object.keys(data as Record<string, unknown>).length > 0;
};

const LeadView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { get } = useApi();
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState<LeadDetailResponse | null>(null);

  const fetchLead = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const response = await get(`/admin/leads/${id}`);
      setDetail(response.data);
    } catch (error) {
      console.error('Error fetching lead:', error);
      toast.error('Failed to load lead');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLead();
  }, [id]);

  const conversationColumns: ColumnDef<LeadConversation>[] = [
    {
      accessorKey: 'created_at',
      header: 'Created',
      cell: ({ row }) =>
        row.original.created_at ? formatDate(row.original.created_at) : 'N/A',
    },
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => row.original.name || '-',
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <Badge variant={row.original.status === 'active' ? 'default' : 'secondary'}>
          {row.original.status || 'unknown'}
        </Badge>
      ),
    },
    {
      accessorKey: 'message_count',
      header: 'Messages',
      cell: ({ row }) => row.original.message_count ?? 0,
    },
    {
      id: 'visitor',
      header: 'Visitor',
      cell: ({ row }) => {
        const visitorId = row.original.visitor_id;
        return visitorId ? <span className="font-mono text-xs">{visitorId.substring(0, 8)}...</span> : '-';
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/admin/conversations/${row.original.id}`)}
        >
          <Eye className="h-4 w-4 mr-1" />
          View
        </Button>
      ),
    },
  ];

  const visitorColumns: ColumnDef<LeadVisitorProfile>[] = [
    {
      accessorKey: 'visitor_id',
      header: 'Visitor ID',
      cell: ({ row }) => {
        const visitorId = row.original.visitor_id;
        return visitorId ? <span className="font-mono text-xs">{visitorId.substring(0, 8)}...</span> : 'N/A';
      },
    },
    {
      accessorKey: 'organization_name',
      header: 'Organization',
      cell: ({ row }) => row.original.organization_name || 'N/A',
    },
    {
      accessorKey: 'first_seen_at',
      header: 'First Seen',
      cell: ({ row }) =>
        row.original.first_seen_at ? formatDate(row.original.first_seen_at) : 'N/A',
    },
    {
      accessorKey: 'last_seen_at',
      header: 'Last Seen',
      cell: ({ row }) =>
        row.original.last_seen_at ? formatDate(row.original.last_seen_at) : 'N/A',
    },
    {
      accessorKey: 'session_count',
      header: 'Sessions',
      cell: ({ row }) => row.original.session_count ?? 0,
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <Button
          variant="ghost"
          size="sm"
          disabled={!row.original.visitor_id}
          onClick={() => row.original.visitor_id && navigate(`/admin/visitors/${row.original.visitor_id}`)}
        >
          <Eye className="h-4 w-4 mr-1" />
          View
        </Button>
      ),
    },
  ];

  const conversationTable = useReactTable({
    data: detail?.conversations ?? [],
    columns: conversationColumns,
    getCoreRowModel: getCoreRowModel(),
  });

  const visitorTable = useReactTable({
    data: detail?.visitor_profiles ?? [],
    columns: visitorColumns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (loading && !detail) {
    return <LoadingState label="Loading lead..." className="min-h-[60vh]" />;
  }

  if (!detail) {
    return (
      <div className="px-6 py-4 w-full">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin?tab=leads')}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to leads
        </Button>
        <div className="mt-6 text-muted-foreground">Lead not found.</div>
      </div>
    );
  }

  const lead = detail.lead;
  const displayName = (() => {
    const parts = [lead.first_name, lead.last_name].filter(Boolean).join(' ').trim();
    if (parts) return parts;
    if (lead.email) return lead.email;
    return 'Unknown contact';
  })();

  return (
    <div className="px-6 py-4 w-full space-y-6">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin?tab=leads')}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to leads
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex flex-col gap-1">
            <span className="text-xl">{displayName}</span>
            <span className="text-xs font-mono text-muted-foreground">
              {lead.id}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <div className="text-muted-foreground text-xs">Organization</div>
              <div>{lead.organization_name || '-'}</div>
            </div>
            <div>
              <div className="text-muted-foreground text-xs">Email</div>
              <div>{lead.email || '-'}</div>
            </div>
            <div>
              <div className="text-muted-foreground text-xs">Company</div>
              <div>{lead.company || '-'}</div>
            </div>
            <div>
              <div className="text-muted-foreground text-xs">Title</div>
              <div>{lead.job_title || '-'}</div>
            </div>
            <div>
              <div className="text-muted-foreground text-xs">Source</div>
              <div>{lead.source || '-'}</div>
            </div>
            <div>
              <div className="text-muted-foreground text-xs">Created</div>
              <div>{lead.created_at ? formatDate(lead.created_at) : '-'}</div>
            </div>
            <div>
              <div className="text-muted-foreground text-xs">Last seen</div>
              <div>{lead.last_seen_at ? formatDate(lead.last_seen_at) : '-'}</div>
            </div>
            <div>
              <div className="text-muted-foreground text-xs">Browser events</div>
              <div>{detail.browser_event_count}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Raw Contact Payload JSON</CardTitle>
        </CardHeader>
        <CardContent>
          {hasJsonContent(lead.provider_profiles) ? (
            renderJsonBlock(lead.provider_profiles)
          ) : (
            <div className="text-sm text-muted-foreground">
              No provider payloads stored for this lead.
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Lead JSON</CardTitle>
        </CardHeader>
        <CardContent>{renderJsonBlock(lead)}</CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            Related visitors ({detail.visitor_profiles.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                {visitorTable.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {visitorTable.getRowModel().rows?.length ? (
                  visitorTable.getRowModel().rows.map((row) => (
                    <TableRow key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={visitorColumns.length} className="h-24 text-center">
                      No visitor profiles for this lead.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            Related conversations ({detail.conversations.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                {conversationTable.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {conversationTable.getRowModel().rows?.length ? (
                  conversationTable.getRowModel().rows.map((row) => (
                    <TableRow key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={conversationColumns.length} className="h-24 text-center">
                      No conversations for this lead.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LeadView;
